import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import InsightsIcon from "@mui/icons-material/Insights";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { alpha, keyframes, useTheme } from "@mui/material/styles";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";

import { postSalesChatbotStream } from "../../services/aiChatbotApi";
import {
  listConversations,
  createConversation,
  getConversation,
  deleteConversation,
  renameConversation,
  appendMessages as apiAppendMessages,
} from "../../services/salesChatHistoryApi";
import type { ChatTurn, ConversationSummary } from "../../types/multiChatbot";
import { AssistantMarkdown } from "../common/AssistantMarkdown";
import { SEARCE_COLORS } from "../../theme";

const STORAGE_KEY = "bd-sales-chatbot-v1";
const CHAT_HISTORY_MESSAGE_LIMIT = 8;
const ASSISTANT_HISTORY_MAX_CHARS = 2000;
const STANDALONE_CHAT_MAX_WIDTH = 780;

const STANDALONE_MARKDOWN_SX = {
  fontSize: "0.9375rem",
  lineHeight: 1.6,
  "& h1": { fontSize: "1.25rem" },
  "& h2": { fontSize: "1.125rem" },
  "& h3, & h4, & h5, & h6": { fontSize: "1rem" },
  "& code": { fontSize: "0.8125rem" },
  "& pre code": { fontSize: "0.75rem" },
  "& table": { fontSize: "0.8125rem" },
} as const;

const AI_CONFIGURED = Boolean(process.env.REACT_APP_AI_CHATBOT_URL?.trim());

const streamCursorBlink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

const dotBounce = keyframes`
  0%, 80%, 100% { opacity: 0.3; transform: translateY(0); }
  40% { opacity: 1; transform: translateY(-3px); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;


const IDLE_PHRASES = [
  "Searching success stories…",
  "Reviewing SOW documents…",
  "Connecting to the latest project signals…",
];

/** Empty-state quick prompts. Each template has a "___" blank the rep fills in
 * with their actual client's industry / tech / engagement type before sending. */
const SUGGESTED_PROMPTS: { label: string; template: string }[] = [
  { label: "Industry case studies", template: "What case studies do we have in the ___ industry?" },
  { label: "Tech stack match", template: "Which projects have we delivered using ___?" },
  { label: "Proof points & ROI", template: "What measurable outcomes or ROI have we delivered in ___ engagements?" },
  { label: "Team & timeline estimate", template: "What's a typical team size and delivery timeline for a ___ project?" },
];

type StreamProgressState = {
  statusText: string;
  thinkingText: string | null;
  toolLabel: string | null;
  provisionalAnswer: string;
};

interface SalesChatMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
  isError?: boolean;
  apiHistoryContent?: string;
  timestamp?: number;
}

function newId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function truncateForHistory(text: string, maxLen: number): string {
  const t = text.trim();
  if (t.length <= maxLen) return t;
  return `${t.slice(0, maxLen)}…`;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function parseStoredSession(raw: string | null): SalesChatMessage[] {
  if (!raw) return [];
  try {
    const data = JSON.parse(raw) as unknown;
    if (!isRecord(data)) return [];
    const messagesRaw = data.messages;
    if (!Array.isArray(messagesRaw)) return [];
    const messages: SalesChatMessage[] = [];
    for (const item of messagesRaw) {
      if (!isRecord(item)) continue;
      const { id, role, text } = item;
      if (typeof id !== "string" || typeof text !== "string") continue;
      if (role !== "user" && role !== "assistant") continue;
      messages.push({
        id,
        role,
        text,
        isError: typeof item.isError === "boolean" ? item.isError : undefined,
        apiHistoryContent: typeof item.apiHistoryContent === "string" ? item.apiHistoryContent : undefined,
        timestamp: typeof item.timestamp === "number" ? item.timestamp : undefined,
      });
    }
    return messages;
  } catch {
    return [];
  }
}

function priorMessagesToChatHistory(messages: SalesChatMessage[]): ChatTurn[] {
  const filtered = messages.filter((m) => !m.isError).slice(-CHAT_HISTORY_MESSAGE_LIMIT);
  while (filtered.length > 0 && filtered[filtered.length - 1].role === "user") {
    filtered.pop();
  }
  const alternating: typeof filtered = [];
  for (const m of filtered) {
    if (alternating.length > 0 && alternating[alternating.length - 1].role === m.role) {
      alternating[alternating.length - 1] = m;
    } else {
      alternating.push(m);
    }
  }
  return alternating.map((m) => ({
    role: m.role,
    content:
      m.role === "assistant"
        ? (m.apiHistoryContent ?? truncateForHistory(m.text, ASSISTANT_HISTORY_MAX_CHARS))
        : m.text.trim(),
  }));
}

function errorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const d = err.response?.data as { detail?: string; message?: string } | undefined;
    if (d?.detail) return String(d.detail);
    if (d?.message) return String(d.message);
    if (err.message) return err.message;
  }
  if (err instanceof Error) return err.message;
  return "Something went wrong. Please try again.";
}

function ThinkingDots(): React.ReactElement {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, py: 0.5 }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            bgcolor: "primary.main",
            animation: `${dotBounce} 1.1s ease-in-out infinite`,
            animationDelay: `${i * 0.15}s`,
          }}
        />
      ))}
    </Box>
  );
}

function IdleWaitStrip({ phrase }: { phrase: string }): React.ReactElement {
  return (
    <Paper
      variant="outlined"
      sx={{
        alignSelf: "flex-start",
        maxWidth: "100%",
        p: 1.5,
        borderRadius: 2,
        bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
        borderColor: "divider",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        <ThinkingDots />
        <AnimatePresence mode="wait">
          <motion.div
            key={phrase}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2 }}
          >
            <Typography variant="body2" color="text.secondary">
              {phrase}
            </Typography>
          </motion.div>
        </AnimatePresence>
      </Box>
      <Box
        sx={{
          mt: 1.25,
          height: 4,
          borderRadius: 1,
          background: (t) =>
            `linear-gradient(90deg, ${alpha(t.palette.primary.main, 0.08)} 0%, ${alpha(t.palette.primary.main, 0.28)} 50%, ${alpha(t.palette.primary.main, 0.08)} 100%)`,
          backgroundSize: "200% 100%",
          animation: `${shimmer} 1.8s ease-in-out infinite`,
        }}
      />
    </Paper>
  );
}

function StreamActivityPanel({
  streamProgress,
  idlePhrase,
  showIdleStrip,
}: {
  streamProgress: StreamProgressState;
  idlePhrase: string;
  showIdleStrip: boolean;
}): React.ReactElement | null {
  const hasActivity =
    streamProgress.statusText.trim() !== "" ||
    streamProgress.toolLabel != null;

  if (showIdleStrip && !hasActivity && !streamProgress.provisionalAnswer) {
    return <IdleWaitStrip phrase={idlePhrase} />;
  }

  if (!hasActivity) return null;

  return (
    <Paper
      variant="outlined"
      sx={{
        alignSelf: "flex-start",
        maxWidth: "100%",
        p: 1.5,
        borderRadius: 2,
        borderLeft: 3,
        borderLeftColor: "primary.main",
        bgcolor: (t) => alpha(t.palette.primary.main, 0.05),
      }}
    >
      <Typography variant="caption" fontWeight={700} color="primary.dark" sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
        <AutoAwesomeOutlinedIcon sx={{ fontSize: 14 }} />
        Working on it
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <AnimatePresence initial={false}>
          {streamProgress.statusText.trim() ? (
            <motion.div
              key="status"
              layout
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
            >
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                <LightbulbOutlinedIcon sx={{ fontSize: 18, color: "text.secondary", mt: 0.15 }} />
                <AssistantMarkdown
                  content={streamProgress.statusText}
                  sx={{ ...STANDALONE_MARKDOWN_SX, "& p": { color: "text.primary", margin: 0 } }}
                />
              </Box>
            </motion.div>
          ) : null}
        </AnimatePresence>

        <AnimatePresence mode="wait">
          {streamProgress.toolLabel ? (
            <motion.div
              key={streamProgress.toolLabel}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <Chip
                size="small"
                icon={<StorageOutlinedIcon sx={{ "&&": { fontSize: 16 } }} />}
                label={`${streamProgress.toolLabel}…`}
                variant="outlined"
                sx={{ borderColor: "primary.light", bgcolor: (t) => alpha(t.palette.primary.main, 0.06) }}
              />
            </motion.div>
          ) : null}
        </AnimatePresence>
      </Box>
    </Paper>
  );
}

export const SalesChatPanel: React.FC = () => {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("md"));

  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<SalesChatMessage[]>(() =>
    typeof localStorage !== "undefined" ? parseStoredSession(localStorage.getItem(STORAGE_KEY)) : []
  );
  const [loading, setLoading] = useState(false);
  const [streamProgress, setStreamProgress] = useState<StreamProgressState | null>(null);
  const [idlePhraseIndex, setIdlePhraseIndex] = useState(0);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);

  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [apiAvailable, setApiAvailable] = useState(false);
  const [convPanelOpen, setConvPanelOpen] = useState(true);
  const [loadingConversations, setLoadingConversations] = useState(false);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ messages }));
    } catch {
      /* quota */
    }
  }, [messages]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages, streamProgress, loading]);

  useEffect(() => {
    if (!streamProgress) return;
    const idle =
      streamProgress.provisionalAnswer === "" &&
      streamProgress.statusText.trim() === "" &&
      streamProgress.thinkingText == null &&
      streamProgress.toolLabel == null;
    if (!idle) return;
    const id = window.setInterval(() => setIdlePhraseIndex((n) => (n + 1) % IDLE_PHRASES.length), 2200);
    return () => window.clearInterval(id);
  }, [streamProgress]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      setShowScrollToBottom(el.scrollHeight - el.scrollTop - el.clientHeight > 200);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const storedToInternal = useCallback(
    (m: { role: string; text: string; api_history_content?: string | null; is_error?: boolean }): SalesChatMessage => ({
      id: newId(),
      role: m.role as "user" | "assistant",
      text: m.text,
      apiHistoryContent: m.api_history_content ?? undefined,
      isError: m.is_error ?? undefined,
    }),
    []
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingConversations(true);
      const result = await listConversations();
      if (cancelled) return;
      if (result === null) {
        setApiAvailable(false);
      } else {
        setApiAvailable(true);
        setConversations(result);
        if (result.length > 0) {
          const conv = await getConversation(result[0].id);
          if (!cancelled && conv) {
            setActiveConversationId(conv.id);
            setMessages(conv.messages.map(storedToInternal));
        
          }
        }
      }
      if (!cancelled) setLoadingConversations(false);
    };
    void load();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNewConversation = useCallback(async () => {
    if (loading) return;
    setMessages([]);
    setStreamProgress(null);

    if (apiAvailable) {
      const conv = await createConversation("New conversation");
      if (conv) {
        setActiveConversationId(conv.id);
        setConversations((prev) => [
          { id: conv.id, title: conv.title, updated_at: conv.updated_at, preview: "" },
          ...prev,
        ]);
      }
    } else {
      setActiveConversationId(null);
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
    }
  }, [loading, apiAvailable]);

  const handleSwitchConversation = useCallback(async (id: string) => {
    if (loading) return;
    const conv = await getConversation(id);
    if (!conv) return;
    setActiveConversationId(conv.id);
    setMessages(conv.messages.map(storedToInternal));

    setStreamProgress(null);
  }, [loading, storedToInternal]);

  const handleDeleteConversation = useCallback(async (id: string) => {
    const ok = await deleteConversation(id);
    if (!ok) return;
    setConversations((prev) => {
      const remaining = prev.filter((c) => c.id !== id);
      if (id === activeConversationId) {
        if (remaining.length > 0) {
          void handleSwitchConversation(remaining[0].id);
        } else {
          setActiveConversationId(null);
          setMessages([]);
      
        }
      }
      return remaining;
    });
  }, [activeConversationId, handleSwitchConversation]);

  const handleClearThread = useCallback(() => {
    if (loading) return;
    setMessages([]);
    setStreamProgress(null);
    setActiveConversationId(null);

    try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
  }, [loading]);

  const handleSend = useCallback(async () => {
    const q = input.trim();
    if (!q || loading || !AI_CONFIGURED) return;

    const chat_history = priorMessagesToChatHistory(messages);
    setInput("");
    setMessages((prev) => [...prev, { id: newId(), role: "user", text: q, timestamp: Date.now() }]);
    setLoading(true);
    setStreamProgress({ statusText: "", thinkingText: null, toolLabel: null, provisionalAnswer: "" });

    let accumulatedAnswer = "";
    let receivedDone = false;

    try {
      for await (const ev of postSalesChatbotStream({ userQuestion: q, chatHistory: chat_history })) {
        if (ev.type === "status") {
          setStreamProgress((p) => (p ? { ...p, statusText: ev.text } : p));
        } else if (ev.type === "thinking") {
          setStreamProgress((p) => (p ? { ...p, thinkingText: ev.text } : p));
        } else if (ev.type === "tool_start") {
          setStreamProgress((p) => (p ? { ...p, toolLabel: ev.label } : p));
        } else if (ev.type === "tool_end") {
          setStreamProgress((p) => (p ? { ...p, toolLabel: null } : p));
        } else if (ev.type === "answer_token") {
          accumulatedAnswer += ev.text;
          setStreamProgress((p) => (p ? { ...p, provisionalAnswer: accumulatedAnswer } : p));
        } else if (ev.type === "error") {
          throw new Error(ev.message);
        } else if (ev.type === "done") {
          receivedDone = true;
          const finalAnswer = accumulatedAnswer.trim() || "(No answer produced.)";
          const apiHistoryContent = truncateForHistory(finalAnswer, ASSISTANT_HISTORY_MAX_CHARS);
          const assistantMsg: SalesChatMessage = {
            id: newId(),
            role: "assistant",
            text: finalAnswer,
            apiHistoryContent,
            timestamp: Date.now(),
          };
          setMessages((prev) => [...prev, assistantMsg]);

          if (apiAvailable) {
            const userMsg: SalesChatMessage = { id: newId(), role: "user", text: q, timestamp: Date.now() };
            let convId = activeConversationId;
            if (!convId) {
              const newConv = await createConversation("New conversation");
              if (newConv) {
                convId = newConv.id;
                setActiveConversationId(convId);
                setConversations((prev) => [
                  { id: newConv.id, title: newConv.title, updated_at: newConv.updated_at, preview: q.slice(0, 80) },
                  ...prev,
                ]);
              }
            }
            if (convId) {
              setConversations((prev) =>
                prev.map((c) =>
                  c.id === convId && c.title === "New conversation"
                    ? { ...c, title: q.slice(0, 60), preview: q.slice(0, 80) }
                    : c
                )
              );
              if (conversations.find((c) => c.id === convId)?.title === "New conversation") {
                void renameConversation(convId, q.slice(0, 60));
              }
              const saved = await apiAppendMessages(convId, [
                { role: userMsg.role, text: userMsg.text, api_history_content: userMsg.apiHistoryContent ?? null },
                { role: assistantMsg.role, text: assistantMsg.text, api_history_content: assistantMsg.apiHistoryContent ?? null },
              ]);
              if (!saved) {
                try {
                  localStorage.setItem(STORAGE_KEY, JSON.stringify({ messages: [...messages, userMsg, assistantMsg] }));
                } catch { /* quota */ }
              }
            }
          }
        }
      }

      if (!receivedDone) {
        if (accumulatedAnswer.trim()) {
          const finalAnswer = accumulatedAnswer.trim();
          setMessages((prev) => [
            ...prev,
            { id: newId(), role: "assistant", text: finalAnswer, apiHistoryContent: truncateForHistory(finalAnswer, ASSISTANT_HISTORY_MAX_CHARS), timestamp: Date.now() },
          ]);
        } else {
          setMessages((prev) => [
            ...prev,
            { id: newId(), role: "assistant", text: "Stream ended without a final response.", isError: true },
          ]);
        }
      }
    } catch (e) {
      const msg = errorMessage(e);
      setMessages((prev) => [...prev, { id: newId(), role: "assistant", text: msg, isError: true }]);
    } finally {
      setStreamProgress(null);
      setLoading(false);
    }
  }, [input, loading, messages, apiAvailable, activeConversationId, conversations]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  const handlePromptChipClick = useCallback((template: string) => {
    setInput(template);
    window.setTimeout(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      const blankStart = template.indexOf("___");
      if (blankStart >= 0) {
        el.setSelectionRange(blankStart, blankStart + 3);
      }
    }, 0);
  }, []);

  const streamIdlePhrase = IDLE_PHRASES[idlePhraseIndex % IDLE_PHRASES.length] ?? IDLE_PHRASES[0];
  const showStreamIdleStrip =
    streamProgress != null &&
    streamProgress.provisionalAnswer === "" &&
    streamProgress.statusText.trim() === "" &&
    streamProgress.thinkingText == null &&
    streamProgress.toolLabel == null;

  const formatRelativeTime = (iso: string): string => {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const conversationsSidebar = (
    <Box
      sx={{
        width: 240,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        borderRight: `1px solid ${theme.palette.divider}`,
        bgcolor: "background.paper",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Conversations
        </Typography>
        <Tooltip title="New conversation">
          <IconButton size="small" onClick={() => void handleNewConversation()} disabled={loading}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto" }}>
        {loadingConversations ? (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 3 }}>
            <CircularProgress size={20} />
          </Box>
        ) : conversations.length === 0 ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", p: 2 }}>
            No conversations yet.
          </Typography>
        ) : (
          <List dense disablePadding>
            {conversations.map((conv) => (
              <ListItemButton
                key={conv.id}
                selected={conv.id === activeConversationId}
                onClick={() => void handleSwitchConversation(conv.id)}
                sx={{
                  py: 0.75,
                  px: 1.5,
                  alignItems: "flex-start",
                  "& .conv-delete": { opacity: 0, transition: "opacity 0.15s" },
                  "&:hover .conv-delete": { opacity: 1 },
                }}
              >
                <ListItemText
                  primary={conv.title}
                  secondary={formatRelativeTime(conv.updated_at)}
                  primaryTypographyProps={{ noWrap: true, variant: "body2", sx: { fontWeight: conv.id === activeConversationId ? 600 : 400 } }}
                  secondaryTypographyProps={{ variant: "caption" }}
                  sx={{ mr: 0.5 }}
                />
                <Tooltip title="Delete" placement="right">
                  <IconButton
                    className="conv-delete"
                    size="small"
                    onClick={(e) => { e.stopPropagation(); void handleDeleteConversation(conv.id); }}
                    sx={{ mt: 0.25, flexShrink: 0, color: "text.secondary" }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </ListItemButton>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );

  const messageBubbles = messages.map((m, i) => {
    const isNewTurn = i > 0 && m.role === "user" && messages[i - 1].role === "assistant";
    const showCopyBtn = m.role === "assistant" && !m.isError;
    return (
      <Box
        key={m.id}
        sx={{
          alignSelf: m.role === "user" ? "flex-end" : "flex-start",
          maxWidth: "92%",
          mt: isNewTurn ? 1.5 : 0,
          ...(showCopyBtn && {
            position: "relative",
            "& .msg-copy-btn": { opacity: 0, transition: "opacity 0.15s" },
            "&:hover .msg-copy-btn": { opacity: 1 },
          }),
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderRadius: 3,
            bgcolor:
              m.role === "user"
                ? alpha(SEARCE_COLORS.primary, 0.12)
                : "background.paper",
            border: "1px solid",
            borderColor: m.isError ? "error.light" : "divider",
            boxShadow: m.role === "assistant" && !m.isError ? "0 1px 2px rgba(0,0,0,0.04)" : "none",
          }}
        >
          {m.role === "assistant" && !m.isError ? (
            <AssistantMarkdown content={m.text} sx={STANDALONE_MARKDOWN_SX} />
          ) : (
            <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {m.text}
            </Typography>
          )}
        </Box>
        {m.timestamp ? (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ display: "block", textAlign: m.role === "user" ? "right" : "left", mt: 0.25, fontSize: "0.6875rem" }}
          >
            {new Intl.DateTimeFormat("en", { hour: "numeric", minute: "2-digit" }).format(new Date(m.timestamp))}
          </Typography>
        ) : null}
        {showCopyBtn ? (
          <Tooltip title={copiedMessageId === m.id ? "Copied!" : "Copy"} placement="right">
            <IconButton
              className="msg-copy-btn"
              size="small"
              onClick={() => {
                void navigator.clipboard.writeText(m.text).then(() => {
                  setCopiedMessageId(m.id);
                  window.setTimeout(() => setCopiedMessageId(null), 2000);
                });
              }}
              sx={{
                position: "absolute",
                top: 4,
                right: { xs: 4, sm: -36 },
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                p: 0.5,
                "&:hover": { bgcolor: "grey.50" },
              }}
            >
              {copiedMessageId === m.id ? (
                <DoneIcon sx={{ fontSize: 14, color: "success.main" }} />
              ) : (
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              )}
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
    );
  });

  const streamSection = streamProgress != null ? (
    <>
      <StreamActivityPanel
        streamProgress={streamProgress}
        idlePhrase={streamIdlePhrase}
        showIdleStrip={Boolean(showStreamIdleStrip)}
      />
      <AnimatePresence mode="wait">
        {streamProgress.thinkingText ? (
          <motion.div
            key={streamProgress.thinkingText}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            style={{ alignSelf: "flex-start", maxWidth: "100%" }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: "italic", px: 0.5, lineHeight: 1.6 }}
            >
              {streamProgress.thinkingText}
            </Typography>
          </motion.div>
        ) : null}
      </AnimatePresence>
      {streamProgress.provisionalAnswer ? (
        <Box
          sx={{
            alignSelf: "flex-start",
            maxWidth: "100%",
            px: 2,
            py: 1.25,
            borderRadius: 3,
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
          }}
        >
          <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            Assistant
          </Typography>
          <Box sx={{ wordBreak: "break-word" }}>
            <AssistantMarkdown content={streamProgress.provisionalAnswer} sx={STANDALONE_MARKDOWN_SX} />
            <Box
              component="span"
              sx={{
                display: "inline-block",
                width: "0.45em",
                height: "1em",
                bgcolor: "text.secondary",
                ml: "1px",
                verticalAlign: "text-bottom",
                animation: `${streamCursorBlink} 1s steps(1, end) infinite`,
              }}
            />
          </Box>
        </Box>
      ) : null}
    </>
  ) : null;

  const emptyState = messages.length === 0 && (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        py: 6,
        px: 2,
        gap: 1,
      }}
    >
      <AutoAwesomeOutlinedIcon sx={{ fontSize: 40, color: "primary.main", opacity: 0.7, mb: 0.5 }} />
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        Sales Insights AI
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: "auto" }}>
        Natural-language answers grounded in success stories, SOW documents, projects, and allocations.
        Start with a suggestion below or type your own question.
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center", mt: 2, maxWidth: 480 }}>
        {SUGGESTED_PROMPTS.map((p) => (
          <Chip
            key={p.label}
            label={p.label}
            variant="outlined"
            clickable
            disabled={!AI_CONFIGURED}
            onClick={() => handlePromptChipClick(p.template)}
            sx={{
              borderColor: "primary.light",
              color: "primary.dark",
              bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
              "&:hover": { bgcolor: (t) => alpha(t.palette.primary.main, 0.1) },
            }}
          />
        ))}
      </Box>
    </Box>
  );

  const composer = (
    <Box
      sx={{
        flexShrink: 0,
        px: { xs: 1.5, sm: 2 },
        pb: 2,
        pt: 0,
        bgcolor: "background.default",
        borderTop: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box sx={{ maxWidth: STANDALONE_CHAT_MAX_WIDTH, mx: "auto", width: "100%" }}>
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: 1,
            p: "6px 8px 6px 14px",
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: "background.paper",
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          }}
        >
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            maxRows={6}
            variant="standard"
            placeholder="Ask anything about sales, projects, or accounts…"
            value={input}
            disabled={loading || !AI_CONFIGURED}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            InputProps={{
              disableUnderline: true,
              sx: { fontSize: "0.9375rem", py: 0.5 },
            }}
          />
          <Tooltip title={loading ? "Sending…" : "Send"}>
            <span>
              <IconButton
                color="primary"
                onClick={() => void handleSend()}
                disabled={loading || !input.trim() || !AI_CONFIGURED}
                aria-label={loading ? "Sending" : "Send"}
                aria-busy={loading}
                sx={{ flexShrink: 0 }}
              >
                {loading ? <CircularProgress color="inherit" size={22} thickness={5} /> : <SendIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Paper>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
        boxSizing: "border-box",
        p: { xs: 1, sm: 2 },
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "row", overflow: "hidden", gap: 0 }}>
        {!isNarrow && convPanelOpen && conversationsSidebar}
        <Drawer
          anchor="left"
          open={isNarrow && convPanelOpen}
          onClose={() => setConvPanelOpen(false)}
          PaperProps={{ sx: { width: 260 } }}
        >
          {conversationsSidebar}
        </Drawer>

        <Paper
          component="main"
          elevation={0}
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            borderRadius: 3,
            overflow: "hidden",
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: "background.paper",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.25,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              flexShrink: 0,
              borderBottom: `1px solid ${theme.palette.divider}`,
              bgcolor: "background.paper",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Tooltip title={convPanelOpen ? "Hide conversations" : "Show conversations"}>
                <IconButton size="small" onClick={() => setConvPanelOpen((p) => !p)}>
                  {convPanelOpen ? <ChevronLeftIcon fontSize="small" /> : <ForumOutlinedIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Typography variant="h6" sx={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 1 }}>
                <InsightsIcon color="primary" />
                Sales Insights AI
              </Typography>
            </Box>
            <Button
              size="medium"
              onClick={handleClearThread}
              disabled={loading || messages.length === 0}
              sx={{ textTransform: "none", minWidth: 0 }}
            >
              Clear
            </Button>
          </Box>

          {!AI_CONFIGURED && (
            <Alert severity="warning" sx={{ mx: 2, mt: 2, mb: 0 }}>
              Set <code>REACT_APP_AI_CHATBOT_URL</code> in your environment (see <code>.env.example</code>).
            </Alert>
          )}

          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", position: "relative" }}>
            <Box
              ref={scrollRef}
              sx={{ flex: 1, overflow: "auto", px: { xs: 1.5, sm: 2 }, py: 2 }}
            >
              <Box
                sx={{
                  maxWidth: STANDALONE_CHAT_MAX_WIDTH,
                  mx: "auto",
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                {emptyState}
                {messageBubbles}
                {streamSection}
              </Box>
            </Box>
            {showScrollToBottom && (
              <Tooltip title="Scroll to latest" placement="left">
                <IconButton
                  onClick={() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" })}
                  size="small"
                  sx={{
                    position: "absolute",
                    bottom: 72,
                    right: 16,
                    zIndex: 10,
                    bgcolor: "background.paper",
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                    "&:hover": { bgcolor: "grey.100" },
                  }}
                >
                  <KeyboardArrowDownIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column" }}>
              {composer}
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};
